import { cva, VariantProps } from 'class-variance-authority'
import { isNil } from 'lodash-es'
import { ComponentProps } from 'react'

import { cn, useFixWidth } from '@/shared/lib'
import { Chip } from '@/shared/ui'

const DEFAULT_OFFSET = 17.5
const LARGE_OFFSET = 15

const filterChipVariants = cva(
  [
    'typo-label1 shrink-0 font-medium text-black',
    '[&_svg]:size-auto [&_svg]:text-gray-300 [&_svg]:transition-colors hover:[&_svg]:text-black',
    'data-[selected=true]:border-purple-500 [&[data-selected=true]_svg]:text-black',
  ],

  {
    variants: {
      size: {
        medium: 'h-11 px-4 py-2.5',
        large: 'h-27 px-2.5 pt-3.5 pb-1.5',
      },
    },
  },
)

type FilterChipVariant = VariantProps<typeof filterChipVariants>

type Props = Omit<ComponentProps<typeof Chip>, 'variant' | 'shape' | 'size'> &
  FilterChipVariant

export const FilterChip = ({
  size = 'medium',
  children,
  className,
  startIcon,
  ...restProps
}: Props) => {
  const offset = (() => {
    if (size === 'large') {
      return LARGE_OFFSET
    }

    // startIcon이 있는 경우 좌측 여백이 4px 줄어듦
    return isNil(startIcon) ? DEFAULT_OFFSET : DEFAULT_OFFSET - 2
  })()

  const { width: widthFromChildren, childrenRef } = useFixWidth<HTMLDivElement>(
    children,
    { offset },
  )

  return (
    <Chip
      variant="outline"
      shape="rounded"
      width={widthFromChildren}
      childrenRef={childrenRef}
      className={cn(filterChipVariants({ size }), className)}
      startIcon={startIcon}
      {...restProps}
    >
      {children}
    </Chip>
  )
}
